import React, { useEffect, useState } from 'react';
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Divider,
  Grid, IconButton, Paper, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { fetchRoles } from '../reduxStore/RoleSlice';
import {
  fetchPermissions, fetchUserPermissions,
  setUserPermission, deleteUserPermission, clearPermissionData, clearPermissionMessages,
} from '../reduxStore/PermissionSlice';
import {
  fetchUserScopes, setUserScopes, clearScopeData, clearScopeMessages,
  UserScopeMap, RestrictOverride,
} from '../reduxStore/ScopeSlice';
import { fetchDepartements } from '../reduxStore/DepartementSlice';
import { fetchGeneriqueDataByType } from '../reduxStore/GeneriqueDataSlice';
import { fetchSysTypeDocuments } from '../reduxStore/SysTypeDocumentSlice';
import { User, Role, Permission, Departement } from '../models/models';
import { groupByDept } from '../utils/scopeGrouping';
import { GeneriqueDataType, ScopeType, ScopeEffect } from '../constants/enums';
import { useSnackbar } from '../components/SnackbarProvider';
import api from '../reduxStore/api';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import AideBulle from '../components/AideBulle';
import { aidePermission, NOTE_RECONNEXION } from '../constants/permissionAide';

const SCOPE_LABELS: Record<ScopeType, string> = {
  DEPARTEMENT:         'Départements',
  DEPOT:               'Dépôts',
  CAISSE:              'Caisses',
  TYPE_DOCUMENT:       'Types de document',
  RESTRICT_TO_CREATOR: 'Restriction propriétaire',
};

// Cycle au clic : undefined → BLOCK → ALLOW → undefined
const cycle = (current: ScopeEffect | undefined): ScopeEffect | undefined =>
  current === undefined ? ScopeEffect.BLOCK
  : current === ScopeEffect.BLOCK ? ScopeEffect.ALLOW
  : undefined;

// Convertit la map d'overrides en deux tableaux pour l'API
const splitMap = (m: UserScopeMap): { blocks: number[]; allows: number[] } => {
  const blocks: number[] = [];
  const allows: number[] = [];
  Object.entries(m).forEach(([k, v]) => {
    const id = Number(k);
    if (v === 'BLOCK') blocks.push(id); else if (v === 'ALLOW') allows.push(id);
  });
  return { blocks, allows };
};

interface TriItem { id: number; label: string; departement_id?: number | null; }

interface TriListProps {
  title:    string;
  items:    TriItem[];
  emptyMsg: string;
  state:    UserScopeMap;
  onChange: (id: number) => void;
  fullWidth?: boolean;
  /** Si fourni, regroupe les items par dept (nécessite items avec departement_id). */
  departements?:    Departement[];
  /** dept_ids dont les items doivent être grisés/disabled (déjà bloqués au niveau dept). */
  blockedDeptIds?:  number[];
}

const TriItemRender: React.FC<{
  it:        TriItem;
  state:     UserScopeMap;
  onChange:  (id: number) => void;
  disabled?: boolean;
}> = ({ it, state, onChange, disabled }) => {
  const eff = state[it.id];
  const tooltip = disabled         ? 'Couvert par le scope département'
                : eff === 'BLOCK'  ? 'Bloqué (force)'
                : eff === 'ALLOW'  ? 'Autorisé (override du rôle)'
                : 'Hérité du rôle';
  const color = disabled         ? 'text.disabled'
              : eff === 'BLOCK'  ? 'error.main'
              : eff === 'ALLOW'  ? 'success.main'
              : 'text.secondary';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: disabled ? 0.5 : 1 }}>
      <Tooltip title={tooltip} placement="left">
        <span>
          <IconButton size="small" onClick={() => onChange(it.id)} sx={{ color }} disabled={disabled}>
            {eff === 'BLOCK' ? <BlockIcon fontSize="small" />
             : eff === 'ALLOW' ? <CheckCircleIcon fontSize="small" />
             : <RadioButtonUncheckedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Typography variant="body2" fontWeight={500} noWrap sx={{ color }}>
        {it.label}
      </Typography>
    </Box>
  );
};

const TriList: React.FC<TriListProps> = ({ title, items, emptyMsg, state, onChange, fullWidth, departements, blockedDeptIds }) => {
  const grouped = !!departements && departements.length > 0;
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>{title}</Typography>
      {items.length === 0 ? (
        <Typography variant="caption" color="text.secondary">{emptyMsg}</Typography>
      ) : grouped ? (
        groupByDept(items, departements!).map(g => {
          const deptBlocked = g.dept ? !!blockedDeptIds?.includes(g.dept.id!) : false;
          return (
            <Box key={g.dept?.id ?? 'shared'} sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ display: 'block', textTransform: 'uppercase', fontSize: '0.7rem', color: 'text.secondary', mt: 0.5, mb: 0.25 }}>
                {g.dept ? g.dept.nom : 'Partagé'}{deptBlocked && ' (dept bloqué)'}
              </Typography>
              <Box sx={{ ml: 0.5 }}>
                {g.items.map(it => (
                  <TriItemRender key={it.id} it={it} state={state} onChange={onChange} disabled={deptBlocked} />
                ))}
              </Box>
            </Box>
          );
        })
      ) : (
        <Box sx={fullWidth ? {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          columnGap: 2, rowGap: 0.5,
        } : {}}>
          {items.map(it => (
            <TriItemRender key={it.id} it={it} state={state} onChange={onChange} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const GestionUserDroits: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { roleData } = useSelector((state: RootState) => state.RoleSlice);
  const { permissionData, userPermissions, error, successMessage } =
    useSelector((state: RootState) => state.PermissionSlice);
  const {
    userScopesDept, userScopesDepot, userScopesCaisse, userScopesTypeDoc, userScopesRestrictToCreator,
    error: scopeError, successMessage: scopeSuccess,
  } = useSelector((state: RootState) => state.ScopeSlice);
  const { departements }          = useSelector((state: RootState) => state.DepartementSlice);
  const { depotData, caisseData } = useSelector((state: RootState) => state.GeneriqueDataSlice);
  const { sysTypeData }           = useSelector((state: RootState) => state.SysTypeDocumentSlice);
  const { showSnackbar } = useSnackbar();

  const [users, setUsers]               = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userRoles, setUserRoles]       = useState<Role[]>([]);
  const [loadingUser, setLoadingUser]   = useState(false);

  const [editDept,    setEditDept]    = useState<UserScopeMap>({});
  const [editDepot,   setEditDepot]   = useState<UserScopeMap>({});
  const [editCaisse,  setEditCaisse]  = useState<UserScopeMap>({});
  const [editTypeDoc, setEditTypeDoc] = useState<UserScopeMap>({});
  const [editRestrict, setEditRestrict] = useState<RestrictOverride>('INHERIT');

  useEffect(() => {
    dispatch(fetchRoles({ limit: 500, offset: 0 }));
    dispatch(fetchPermissions({ limit: 500, offset: 0 }));
    dispatch(fetchDepartements());
    dispatch(fetchGeneriqueDataByType({ type: GeneriqueDataType.DEPOT }));
    dispatch(fetchGeneriqueDataByType({ type: GeneriqueDataType.CAISSE }));
    dispatch(fetchSysTypeDocuments());
    api.get('/users/by-owner').then(r => setUsers(r.data.data || []));
    return () => {
      dispatch(clearPermissionData());
      dispatch(clearScopeData());
    };
  }, [dispatch]);

  useEffect(() => {
    if (error) showSnackbar(error, 'error');
    if (successMessage) showSnackbar(successMessage, 'success');
    if (error || successMessage) dispatch(clearPermissionMessages());
  }, [error, successMessage, dispatch, showSnackbar]);

  useEffect(() => {
    if (scopeError)   showSnackbar(scopeError, 'error');
    if (scopeSuccess) showSnackbar(scopeSuccess, 'success');
    if (scopeError || scopeSuccess) dispatch(clearScopeMessages());
  }, [scopeError, scopeSuccess, dispatch, showSnackbar]);

  useEffect(() => {
    if (!selectedUser) return;
    setLoadingUser(true);
    Promise.all([
      api.get(`/roles/user/${selectedUser.id}`).then(r => setUserRoles(r.data.data || [])),
      dispatch(fetchUserPermissions(selectedUser.id)),
      dispatch(fetchUserScopes({ user_id: selectedUser.id, scope_type: ScopeType.DEPARTEMENT })),
      dispatch(fetchUserScopes({ user_id: selectedUser.id, scope_type: ScopeType.DEPOT })),
      dispatch(fetchUserScopes({ user_id: selectedUser.id, scope_type: ScopeType.CAISSE })),
      dispatch(fetchUserScopes({ user_id: selectedUser.id, scope_type: ScopeType.TYPE_DOCUMENT })),
      dispatch(fetchUserScopes({ user_id: selectedUser.id, scope_type: ScopeType.RESTRICT_TO_CREATOR })),
    ]).finally(() => setLoadingUser(false));
  }, [selectedUser, dispatch]);

  useEffect(() => { setEditDept(userScopesDept);       }, [userScopesDept]);
  useEffect(() => { setEditDepot(userScopesDepot);     }, [userScopesDepot]);
  useEffect(() => { setEditCaisse(userScopesCaisse);   }, [userScopesCaisse]);
  useEffect(() => { setEditTypeDoc(userScopesTypeDoc); }, [userScopesTypeDoc]);
  useEffect(() => { setEditRestrict(userScopesRestrictToCreator); }, [userScopesRestrictToCreator]);

  const cycleItem = (setMap: React.Dispatch<React.SetStateAction<UserScopeMap>>) => (id: number) => {
    setMap(prev => {
      const next = { ...prev };
      const newEff = cycle(prev[id]);
      if (newEff === undefined) delete next[id];
      else                      next[id] = newEff;
      return next;
    });
  };

  const handleSaveScopes = async (scopeType: ScopeType, blocks: number[], allows: number[]) => {
    if (!selectedUser) return;
    await dispatch(setUserScopes({ user_id: selectedUser.id, scope_type: scopeType, blocks, allows }));
  };

  const handleAssignRoles = async (roles: Role[]) => {
    if (!selectedUser) return;
    setUserRoles(roles);
    await api.post(`/roles/user/${selectedUser.id}`, { role_ids: roles.map(r => r.id) });
    showSnackbar('Rôles mis à jour.', 'success');
  };

  const handleToggleOverride = async (perm: Permission, granted: boolean) => {
    if (!selectedUser) return;
    await dispatch(setUserPermission({ user_id: selectedUser.id, permission_id: perm.id, granted }));
    dispatch(fetchUserPermissions(selectedUser.id));
  };

  const handleRemoveOverride = async (perm: Permission) => {
    if (!selectedUser) return;
    await dispatch(deleteUserPermission({ user_id: selectedUser.id, permission_id: perm.id }));
    dispatch(fetchUserPermissions(selectedUser.id));
  };

  const onSaveAll = async () => {
    const dept    = splitMap(editDept);
    const depot   = splitMap(editDepot);
    const caisse  = splitMap(editCaisse);
    const typeDoc = splitMap(editTypeDoc);
    await handleSaveScopes(ScopeType.DEPARTEMENT,   dept.blocks,    dept.allows);
    await handleSaveScopes(ScopeType.DEPOT,         depot.blocks,   depot.allows);
    await handleSaveScopes(ScopeType.CAISSE,        caisse.blocks,  caisse.allows);
    await handleSaveScopes(ScopeType.TYPE_DOCUMENT, typeDoc.blocks, typeDoc.allows);
    // RESTRICT_TO_CREATOR : un seul placeholder scope_id=0
    if (editRestrict === 'INHERIT')      await handleSaveScopes(ScopeType.RESTRICT_TO_CREATOR, [],  []);
    else if (editRestrict === 'BLOCK')   await handleSaveScopes(ScopeType.RESTRICT_TO_CREATOR, [0], []);
    else /* ALLOW */                     await handleSaveScopes(ScopeType.RESTRICT_TO_CREATOR, [],  [0]);
  };

  const deptItems    = departements.map(d => ({ id: d.id!, label: d.nom! }));
  const depotItems   = depotData.map(d => ({ id: d.id!, label: d.nom || '', departement_id: d.departement_id }));
  const caisseItems  = caisseData.map(c => ({ id: c.id!, label: c.nom || '', departement_id: c.departement_id }));
  const typeDocItems = sysTypeData.map(t => ({ id: t.id, label: t.libelle }));
  // Depts explicitement bloqués pour CET utilisateur (override BLOCK).
  // Les depots/caisses de ces depts seront grisés dans leurs listes.
  const blockedDeptIds = Object.entries(editDept)
    .filter(([, v]) => v === 'BLOCK')
    .map(([k]) => Number(k));

  return (
    <AppPage>
      <AppPageHeader title="Droits utilisateur" icon={<ManageAccountsIcon />} />

      <AppSurface sx={{ mb: 3 }} padding={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={users}
              getOptionLabel={u => `${u.login} (${u.email})`}
              value={selectedUser}
              onChange={(_, v) => setSelectedUser(v)}
              renderInput={params => <TextField {...params} label="Sélectionner un utilisateur" fullWidth />}
            />
          </Grid>
        </Grid>

        {loadingUser && <CircularProgress sx={{ mt: 2 }} />}

        {selectedUser && !loadingUser && (
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Rôles */}
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>Rôles assignés</Typography>
                <Autocomplete
                  multiple
                  options={roleData}
                  getOptionLabel={r => r.nom}
                  value={userRoles}
                  onChange={(_, v) => handleAssignRoles(v)}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={params => <TextField {...params} label="Rôles" fullWidth />}
                  renderTags={(value, getTagProps) =>
                    value.map((r, i) => <Chip label={r.nom} {...getTagProps({ index: i })} />)
                  }
                />
              </Paper>
            </Grid>

            {/* Overrides individuels permissions */}
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Overrides individuels ({userPermissions.length})
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Ces overrides priment sur les rôles.
                </Typography>
                {userPermissions.map(up => (
                  <Box key={up.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Switch
                      size="small"
                      checked={!!up.granted}
                      onChange={e => handleToggleOverride(up, e.target.checked)}
                      color={up.granted ? 'success' : 'error'}
                    />
                    <Chip label={up.description} size="small" variant="outlined" />
                    <AideBulle titre={up.description || up.code} aide={aidePermission(up)} note={NOTE_RECONNEXION} />
                    <Button size="small" color="error" onClick={() => handleRemoveOverride(up)}>
                      Retirer
                    </Button>
                  </Box>
                ))}

                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">Ajouter un override :</Typography>
                <Box sx={{ mt: 1 }}>
                  <Autocomplete
                    options={permissionData.filter(p => !userPermissions.find(u => u.id === p.id))}
                    getOptionLabel={p => p.description || p.code}
                    onChange={(_, p) => p && handleToggleOverride(p, true)}
                    renderInput={params => <TextField {...params} size="small" label="Permission" />}
                    size="small"
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Scopes directs — 3 états */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Scopes d'accès directs
                  </Typography>
                  <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={onSaveAll}>
                    Enregistrer les scopes
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary">Légende :</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                    <Typography variant="caption">Hérité du rôle</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BlockIcon fontSize="small" color="error" />
                    <Typography variant="caption">Forcé bloqué (additif au rôle)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                    <Typography variant="caption">Forcé autorisé (override du rôle)</Typography>
                  </Box>
                </Box>

                {/* RESTRICT_TO_CREATOR — icône tri-état (cohérent avec les listes) */}
                <Box sx={{ p: 1.5, mb: 2, borderRadius: 1, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip
                      placement="left"
                      title={
                        editRestrict === 'BLOCK' ? 'Restriction forcée ACTIVE'
                        : editRestrict === 'ALLOW' ? 'Restriction forcée DÉSACTIVÉE (override du rôle)'
                        : 'Hérité du rôle'
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => setEditRestrict(prev =>
                          prev === 'INHERIT' ? 'BLOCK' : prev === 'BLOCK' ? 'ALLOW' : 'INHERIT'
                        )}
                        sx={{
                          color: editRestrict === 'BLOCK' ? 'error.main'
                               : editRestrict === 'ALLOW' ? 'success.main'
                               : 'text.secondary',
                        }}
                      >
                        {editRestrict === 'BLOCK' ? <BlockIcon />
                         : editRestrict === 'ALLOW' ? <CheckCircleIcon />
                         : <RadioButtonUncheckedIcon />}
                      </IconButton>
                    </Tooltip>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {SCOPE_LABELS.RESTRICT_TO_CREATOR}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Si activée, l'utilisateur ne voit que les documents qu'il a lui-même créés.
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TriList title={SCOPE_LABELS.DEPARTEMENT} items={deptItems} emptyMsg="Aucun département"
                      state={editDept} onChange={cycleItem(setEditDept)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TriList title={SCOPE_LABELS.DEPOT} items={depotItems} emptyMsg="Aucun dépôt"
                      state={editDepot} onChange={cycleItem(setEditDepot)}
                      departements={departements} blockedDeptIds={blockedDeptIds} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TriList title={SCOPE_LABELS.CAISSE} items={caisseItems} emptyMsg="Aucune caisse"
                      state={editCaisse} onChange={cycleItem(setEditCaisse)}
                      departements={departements} blockedDeptIds={blockedDeptIds} />
                  </Grid>
                  <Grid item xs={12}>
                    <TriList title={SCOPE_LABELS.TYPE_DOCUMENT} items={typeDocItems} emptyMsg="Aucun type"
                      state={editTypeDoc} onChange={cycleItem(setEditTypeDoc)} fullWidth />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}
      </AppSurface>
    </AppPage>
  );
};

export default GestionUserDroits;
